/**
 * ElementPreview — Renders a single form element in design-time preview mode.
 *
 * Pure component extracted from the 3705-line AdvancedFormBuilder monolith.
 * Reads ONLY from the `element` prop — no closure state from the parent.
 */
import React from 'react';

const ElementPreview = ({ element }) => {
  const style = {
    ...element.styling,
    textAlign: element.textAlign || 'left',
  };

  const containerStyle = {
    display: 'flex',
    justifyContent:
      element.alignment === 'center' ? 'center' :
      element.alignment === 'right'  ? 'flex-end' :
                                       'flex-start',
    width: '100%',
  };

  const renderContent = () => {
    switch (element.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tel':
      case 'url':
      case 'password':
      case 'date':
      case 'color':
      case 'range':
        return <input type={element.type} placeholder={element.placeholder} style={style} disabled />;
      case 'textarea':
        return <textarea placeholder={element.placeholder} style={style} disabled />;
      case 'select':
        return (
          <select style={style} disabled>
            {(element.options || []).map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <label className="checkbox-preview" style={{ textAlign: element.textAlign }}>
            <input type="checkbox" style={{ ...style, width: 'auto' }} disabled />
            {element.label}
          </label>
        );
      case 'radio':
        return (
          <div style={{ textAlign: element.textAlign }}>
            {(element.options || []).map((option, idx) => (
              <label key={idx} className="radio-preview">
                <input type="radio" name={`radio-${element.id}`} style={{ ...style, width: 'auto' }} disabled />
                {option}
              </label>
            ))}
          </div>
        );
      case 'file':
        return <input type="file" style={style} disabled />;
      case 'image':
        return (
          <div className="image-preview" style={{ textAlign: element.alignment }}>
            {element.imageUrl ? (
              <img src={element.imageUrl} alt={element.altText} style={style} />
            ) : (
              <div className="image-placeholder" style={{
                ...style, backgroundColor: '#f7fafc', border: '2px dashed #cbd5e0',
                display: 'inline-block', alignItems: 'center', justifyContent: 'center',
                color: '#718096', fontSize: '14px', minHeight: '100px', textAlign: 'center',
              }}>Click to upload image</div>
            )}
          </div>
        );
      case 'video':
        return (
          <div className="video-preview" style={{ textAlign: element.alignment }}>
            {element.videoUrl ? (
              <video src={element.videoUrl} controls style={style} />
            ) : (
              <div className="video-placeholder" style={{
                ...style, backgroundColor: '#f7fafc', border: '2px dashed #cbd5e0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#718096', fontSize: '14px', minHeight: '100px', textAlign: 'center',
              }}>Click to upload video</div>
            )}
          </div>
        );
      case 'map':
        return (
          <div className="map-preview" style={{ textAlign: element.alignment }}>
            <div style={{
              ...style, backgroundColor: '#e2e8f0', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: '#718096', fontSize: '14px',
            }}>
              Map Preview (Lat: {element.lat}, Lng: {element.lng}, Zoom: {element.zoom})
            </div>
          </div>
        );
      case 'table':
        return (
          <table style={style}>
            <tbody>
              {(element.content || []).map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} style={element.cellStyle}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      case 'link':
        return (
          <a href={element.href} style={style} onClick={(e) => e.preventDefault()}>
            {element.text}
          </a>
        );
      case 'container':
        return (
          <div style={style}>
            <div style={{
              backgroundColor: '#f7fafc', padding: '10px', border: '1px dashed #cbd5e0',
              textAlign: 'center', color: '#718096',
            }}>Container (Empty)</div>
          </div>
        );
      case 'header': {
        const HeaderTag = element.headerLevel || 'h2';
        return <HeaderTag style={style}>{element.label}</HeaderTag>;
      }
      case 'divider':
        return <hr style={style} />;
      case 'button':
      case 'submit':
        return (
          <div style={{ textAlign: element.alignment }}>
            <button type={element.buttonType || 'button'} style={style} disabled>
              {element.buttonText}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return <div style={containerStyle}>{renderContent()}</div>;
};

export default ElementPreview;
